import type { PlayerId } from "@/types/branded";
import { generateUid } from "@/utils/index";
import type { MatchId } from "../types";
import type { Edge } from "../types/edge";
import type { KnockoutGraph } from "../types/graph";
import type { KnockoutNode } from "../types/node";

type Input =
  | { type: "player"; player: PlayerId }
  | { type: "match"; id: MatchId; outcome: "winner" | "loser" }
  | { type: "bye" };

export const generateKnockoutGraph = (players: PlayerId[], eliminations: number): KnockoutGraph => {
  if (players.length === 0 || eliminations <= 0) return { nodes: new Map(), edges: [] };

  const nodes = new Map<MatchId, KnockoutNode>();
  const edges: Edge[] = [];

  const seeds = prepareSeeds(players);
  let currentInputs: Input[] = seeds.map((p) =>
    p ? { type: "player", player: p } : { type: "bye" },
  );

  const bracketWinners: MatchId[] = [];

  for (let b = 0; b < eliminations; b++) {
    const { winner, realLoserMatchIds } = buildBracket(currentInputs, b, nodes, edges);
    if (winner) bracketWinners.push(winner);
    currentInputs = realLoserMatchIds.map((id) => ({ type: "match", id, outcome: "loser" }));
    if (currentInputs.length === 0) break;
  }

  buildStepladder(nodes, edges, bracketWinners, eliminations);

  return pruneByes({ nodes, edges });
};

const buildBracket = (
  inputs: Input[],
  bracket: number,
  nodes: Map<MatchId, KnockoutNode>,
  edges: Edge[],
) => {
  let layer = inputs;
  let lastMatchId: MatchId | null = null;
  const realLoserMatchIds: MatchId[] = [];
  let layerIsReal = inputs.map((input) => input.type !== "bye");

  while (layer.length > 1) {
    const nextLayer: Input[] = [];
    const nextLayerIsReal: boolean[] = [];

    for (let i = 0; i < layer.length; i += 2) {
      const left = layer[i];
      const right = layer[i + 1];
      const leftIsReal = layerIsReal[i];
      const rightIsReal = layerIsReal[i + 1];

      const id = generateUid() as MatchId;
      nodes.set(id, { id, type: "knockout", bracket, isChampionship: false });
      lastMatchId = id;

      attach(left, id, 0, edges);
      attach(right, id, 1, edges);

      if (leftIsReal && rightIsReal) {
        realLoserMatchIds.push(id);
      }

      nextLayer.push({ type: "match", id, outcome: "winner" });
      nextLayerIsReal.push(!!leftIsReal || !!rightIsReal);
    }

    layer = nextLayer;
    layerIsReal = nextLayerIsReal;
  }

  return { winner: lastMatchId, realLoserMatchIds };
};

const attach = (input: Input | undefined, to: MatchId, slot: 0 | 1, edges: Edge[]) => {
  if (!input) return;
  if (input.type === "player") {
    edges.push({ kind: "seed", player: input.player, to, slot });
  } else if (input.type === "match") {
    edges.push({ kind: "match", from: input.id, to, outcome: input.outcome, slot });
  }
};

const prepareSeeds = (players: PlayerId[]): (PlayerId | null)[] => {
  const size = 2 ** Math.ceil(Math.log2(players.length || 1));
  let indices = [0];
  for (let i = 1; i < size; i *= 2) {
    indices = indices.flatMap((s) => [s, 2 * i - 1 - s]);
  }
  return indices.map((i) => (i < players.length ? players[i]! : null));
};

const buildStepladder = (
  nodes: Map<MatchId, KnockoutNode>,
  edges: Edge[],
  winners: MatchId[],
  elims: number,
) => {
  if (winners.length < 2) return;
  let survivor = winners[winners.length - 1]!;
  for (let i = winners.length - 2; i >= 0; i--) {
    const boss = winners[i]!;
    for (let l = 0; l < elims - i; l++) {
      const id = `m-champ-${i}-${l}` as MatchId;
      nodes.set(id, { id, type: "knockout", bracket: elims, isChampionship: true });
      edges.push({ kind: "match", from: survivor, to: id, outcome: "winner", slot: 0 });
      edges.push({ kind: "match", from: boss, to: id, outcome: "winner", slot: 1 });
      survivor = id;
    }
  }
};

const pruneByes = (graph: KnockoutGraph): KnockoutGraph => {
  const { nodes, edges } = graph;
  const prunedEdges: Edge[] = [];
  const activeNodes = new Map<MatchId, KnockoutNode>();

  const getIncoming = (id: MatchId) => edges.filter((e) => e.to === id);

  const isByeNode = (id: MatchId) => {
    const node = nodes.get(id);
    return node && !node.isChampionship && getIncoming(id).length < 2;
  };

  const resolveSource = (edge: Edge): Edge | null => {
    if (edge.kind === "seed" || !isByeNode(edge.from)) return edge;
    const incoming = getIncoming(edge.from);
    if (incoming.length === 0) return null;
    return resolveSource({ ...incoming[0], to: edge.to, slot: edge.slot } as Edge);
  };

  for (const node of nodes.values()) {
    if (isByeNode(node.id)) continue;
    const incoming = getIncoming(node.id);
    for (const e of incoming) {
      const resolved = resolveSource(e);
      if (resolved) {
        prunedEdges.push(resolved);
        activeNodes.set(node.id, node);
      }
    }
  }

  return { nodes: activeNodes, edges: prunedEdges };
};
